<template>
  <b-tabs
    v-if="!isEmpty(associationsList)"
    v-model="activeTabIndex"
    class="mt-3 vt-tabs sr-detail-tabs sidepanel-tabs"
    content-class="sidepanel-tabs__content"
  >
    <b-tab
      v-for="(associationType, index) in associationTypes"
      :key="index"
      :title="associationType.translatedName"
    >
      <b-row class="my-2">
        <b-col
          v-if="showCenterFilter && laboratories"
          cols="12"
        >
          <div class="form-label">
            <label>{{ $tc('configuration.grid.center', 1) }}</label>
          </div>
          <vt-combo-select
            v-model="labSelected"
            track-by="labId"
            :options="laboratories"
            label="labName"
          />
        </b-col>
        <b-col class="d-flex align-items-center mt-3">
          <span
            v-if="activeTabIndex === ROLES_TAB"
            class="dark"
          >{{ `${internalAssociationsList[associationType.key].length} ${associationType.translatedName}` }}</span>
          <span
            v-else-if="activeTabIndex === OPERATIONS_TAB"
            class="dark"
          >{{ `${filteredInternalOperationsList.length} ${associationType.translatedName}` }}</span>
          <b-button
            v-tooltip="$t('configuration.actions.update')"
            class="btn__icon btn__icon--primary  pl-3"
            @click="$emit('update', associationType)"
          >
            <i
              class="far fa-edit"
              aria-hidden="true"
            />
          </b-button>
        </b-col>
        <operation-group-filter
          v-if="showOperationGroupFilter && associationType.key === ASSOCIATION_TYPE.OPERATION.key"
          @change="groupFilter = $event"
        />
      </b-row>
      <div class="px-3 scroll-area pt-3">
        <div v-if="associationType.key === ASSOCIATION_TYPE.OPERATION.key">
          <b-row
            v-for="(association, indexAssociation) in filteredInternalOperationsList"
            :key="indexAssociation"
            class="tech-row bottom-bordered py-2"
          >
            <b-col>
              {{ getAssociationName(association[associationType.key]) }}
              <i
                v-if="associationType.key === ASSOCIATION_TYPE.OPERATION.key && getOperationAssociatedRoles(association[ASSOCIATION_TYPE.OPERATION.key], associationsList[ASSOCIATION_TYPE.OPERATION.key]) !== ''"
                v-tooltip="`${$t('configuration.modal.comeFromRole')}:${getOperationAssociatedRoles(association[ASSOCIATION_TYPE.OPERATION.key], associationsList[ASSOCIATION_TYPE.OPERATION.key])}`"
                style="cursor:pointer"
                class="main fas fa-exclamation ml-2"
                aria-hidden="true"
              />
            </b-col>
          </b-row>
        </div>
        <div v-else>
          <b-row
            v-for="(association, indexAssociation) in internalAssociationsList[associationType.key]"
            :key="indexAssociation"
            class="tech-row bottom-bordered py-2"
          >
            <b-col>
              {{ getAssociationName(association[associationType.key], internalAssociationsList) }}
              <i
                v-if="associationType.key === ASSOCIATION_TYPE.OPERATION.key && getOperationAssociatedRoles(association[ASSOCIATION_TYPE.OPERATION.key], associationsList[ASSOCIATION_TYPE.OPERATION.key]) !== ''"
                v-tooltip="`${$t('configuration.modal.comeFromRole')}:${getOperationAssociatedRoles(association[ASSOCIATION_TYPE.OPERATION.key], associationsList[ASSOCIATION_TYPE.OPERATION.key])}`"
                style="cursor:pointer"
                class="main fas fa-exclamation ml-2"
                aria-hidden="true"
              />
            </b-col>
          </b-row>
        </div>
      </div>
    </b-tab>
  </b-tabs>
</template>

<script>
import { cloneDeep, uniqBy, isEmpty } from 'lodash'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import OperationGroupFilter from '@/modules/configurationModule/detail/peopleManagement/OperationGroupFilter'
import { OPERATION_GROUP } from '@/api/resources/configuration/peopleManagement/OperationGroups'
import associationsMixin from '@/modules/configurationModule/detail/peopleManagement/utils/associationsMixin'
const ROLES_TAB = 0
const OPERATIONS_TAB = 1
export default {
    name: 'ConfigurationSidepanelTabs',
    components: { OperationGroupFilter },
    mixins: [ associationsMixin ],
    props: {
        associationTypes: { type: Array, default: () => { return [] } },
        associationsList: { type: Object, default: () => { return {} } },
        showCenterFilter: { type: Boolean },
        showOperationGroupFilter: { type: Boolean }
    },
    data() {
        return {
            internalAssociationsList: {},
            roleLabSelected: { all: true },
            operationLabSelected: { all: true },
            groupFilter: null,
            allLabsRoles: [],
            allLabsOperations: [],
            activeTabIndex: ROLES_TAB
        }
    },
    computed: {
        filteredInternalOperationsList() {
            const list = this.associationsList[ASSOCIATION_TYPE.OPERATION.key]
            let filteredList = []
            if (this.labSelected && this.labSelected.labId) {
                filteredList = list.filter(association => association.laboratory && association.laboratory.labId === this.labSelected.labId)
            } else {
                filteredList = cloneDeep(this.allLabsOperations)
            }
            filteredList = filteredList.filter(association => {
                if (this.groupFilter) {
                    if (this.groupFilter.value === this.OPERATION_GROUP.ACCESS.value) {
                        return association[this.ASSOCIATION_TYPE.OPERATION.key].type === this.OPERATION_GROUP.ACCESS.value
                    } else {
                        return this.getOperationGroup(association[this.ASSOCIATION_TYPE.OPERATION.key]).value === this.groupFilter.value
                    }
                } else {
                    return filteredList
                }
            })
            return uniqBy(filteredList, `operation.id`)
        },
        labSelected: {
            get: function() {
                return this.activeTabIndex === this.ROLES_TAB ? this.roleLabSelected : this.operationLabSelected
            },
            set: function(value) {
                if (this.activeTabIndex === this.ROLES_TAB) {
                    this.roleLabSelected = value
                } else {
                    this.operationLabSelected = value
                }
            }
        },
        laboratories() {
            return this.activeTabIndex === this.ROLES_TAB ? this.roleLaboratories : this.operationLaboratories
        },
        roleLaboratories() {
            let assignedLaboratories = []
            const list = this.associationsList[ASSOCIATION_TYPE.ROLE.key]
            if (list && list.length) {
                assignedLaboratories = uniqBy(list, 'laboratory.labId').map(item => item.laboratory)
                assignedLaboratories.unshift({ labName: this.$t('configuration.elementTitle.allCenters'), all: true })
            }
            return assignedLaboratories
        },
        operationLaboratories() {
            let assignedLaboratories = []
            let assignedLaboratoriesMap = []
            const list = this.associationsList[ASSOCIATION_TYPE.OPERATION.key]
            if (list && list.length) {
                assignedLaboratories = list.filter(association => !association[ASSOCIATION_TYPE.OPERATION.key].global)
                assignedLaboratoriesMap = uniqBy(assignedLaboratories, 'laboratory.labId').map(item => item.laboratory)
                assignedLaboratoriesMap.unshift({ labName: this.$t('configuration.elementTitle.allCenters'), all: true })
            }
            return assignedLaboratoriesMap
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        },
        OPERATION_GROUP() {
            return OPERATION_GROUP
        },
        ROLES_TAB() {
            return ROLES_TAB
        },
        OPERATIONS_TAB() {
            return OPERATIONS_TAB
        }
    },
    watch: {
        associationsList() {
            this.associationTypes.forEach(associationType => {
                this.internalAssociationsList[associationType.key] = cloneDeep(uniqBy(this.associationsList[associationType.key], `${associationType.key}.id`))
                if (this.showCenterFilter) {
                    if (this.internalAssociationsList[associationType.key].length) {
                        let allLabsAssociation = []
                        const associationLab = associationType.key === this.ASSOCIATION_TYPE.ROLE.key ? this.roleLaboratories : this.operationLaboratories
                        this.internalAssociationsList[associationType.key].forEach(association => {
                            const associationParsed = this.mapAssociation(association, associationLab)
                            this.setAssociationLaboratories(associationParsed, this.associationsList[associationType.key], associationType.key, associationLab, this.laboratories)
                            allLabsAssociation.push(associationParsed)
                        })
                        if (associationType.key === this.ASSOCIATION_TYPE.ROLE.key) {
                            this.allLabsRoles = allLabsAssociation.filter(role => role.laboratories.find(lab => lab.assigned && lab.all))
                            this.roleLabSelected = this.roleLaboratories.find(roleLab => roleLab.all)
                        } else {
                            this.allLabsOperations = allLabsAssociation.filter(role => role.laboratories.find(lab => lab.assigned && lab.all))
                            this.operationLabSelected = this.operationLaboratories.find(operationLab => operationLab.all)
                        }
                        this.internalAssociationsList[associationType.key] = cloneDeep(allLabsAssociation)
                    }
                }
            })
        },
        labSelected() {
            if (this.activeTabIndex === this.ROLES_TAB) {
                const list = this.associationsList[ASSOCIATION_TYPE.ROLE.key]
                if (this.labSelected && this.labSelected.labId) {
                    this.internalAssociationsList[ASSOCIATION_TYPE.ROLE.key] = uniqBy(list.filter(item => item.laboratory.labId === this.labSelected.labId), `${ASSOCIATION_TYPE.ROLE.key}.id`)
                } else {
                    this.internalAssociationsList[ASSOCIATION_TYPE.ROLE.key] = cloneDeep(this.allLabsRoles)
                }
            }
        }
    },
    methods: {
        getAssociationName(association) {
            return association.translatedName ? association.translatedName : association.name
        },
        getOperationAssociatedRoles(operation) {
            let textHtml = ''
            const list = this.associationsList ? this.associationsList[ASSOCIATION_TYPE.OPERATION.key] : this.internalList
            const associatedRoles = uniqBy(list.filter(association => association.operation.id === operation.id).map(association => association.role), 'operation.id')
            associatedRoles.forEach(associatedRole => {
                if (associatedRole) textHtml += `<p>${associatedRole.name}</p>`
            })
            return textHtml
        },
        mapAssociation(association, laboratories) {
            let parsedAssociation = cloneDeep(association)
            parsedAssociation.laboratories = laboratories.map(lab => {
                let parsedLab = {}
                parsedLab.labId = lab ? lab.labId : null
                parsedLab.assigned = false
                parsedLab.roles = []
                if (lab && lab.all) parsedLab.all = lab.all
                return parsedLab
            })
            return parsedAssociation
        },
        getOperationGroup(associationOperation) {
            let operationGroup = ''
            let regExp = ''
            for (let operation in this.OPERATION_GROUP) {
                if (this.OPERATION_GROUP[operation].groups) {
                    this.OPERATION_GROUP[operation].groups.forEach(group => {
                        regExp = new RegExp('^' + associationOperation.group)
                        if (regExp.test(group)) operationGroup = this.OPERATION_GROUP[operation]
                    })
                }
            }
            return operationGroup
        },
        isEmpty(value) {
            return isEmpty(value)
        }
    }
}
</script>

<style>
    .configuration-sidepanel .sidepanel-tabs .scroll-area {
        height: calc(100vh - 320px);
    }
</style>
